import { formatDurationDe } from '../../../shared/lib/durationDe'
import { AttachmentBadge } from '../../../shared/ui/feedback/AttachmentBadge'
import { RESCHEDULING_LABEL } from '../model/blockView'
import { resolveRunningMarker, type WorkFit } from '../model/blockDensity'
import { textFor } from './BlockSegment'

/**
 * What the work segment says, at the size it happens to be.
 *
 * Shared by Termin and Aufgabe, which differ only in what they call the subject. The rows are handed
 * out from the count `resolveWorkDensity` measured, cheapest first, and a row that was not paid for
 * is not drawn — that, and nothing about overflow, is what stops the address being sliced in half.
 *
 * Order of spending: the time always, then the subject, then who to ask for, then how to reach them.
 * A worker looking at this on a phone between two jobs wants when and where before who.
 */

interface Props {
  fit: WorkFit
  /**
   * The one-day view. The block has the full width, so the width pays for what the height could not:
   * the duration sits beside the time, and tenant and phone share one row instead of taking two.
   * A row that frees up goes to the subject, as it does in the narrow layout.
   */
  wide?: boolean
  /** Minutes of work, for the wide layout's `· 1 Std. 15 Min.`. */
  workMinutes?: number
  timeLabel: string
  /** The location for a Termin, the title for an Aufgabe. */
  subject: string | null
  /** Usually the tenant's name. */
  who?: string | null
  /** Usually the tenant's phone number. */
  contact?: string | null
  running?: boolean
  /** Displaced and waiting for a new slot — the amber `Wird umgeplant` pill. */
  rescheduling?: boolean
  attachmentCount?: number
}

/** The running marker, inline and after the time so a narrow block truncates the badge, not the time. */
const RunningMarker = ({ withWord, wide = false }: { withWord: boolean; wide?: boolean }) => (
  <span
    className={`inline-flex shrink-0 items-center gap-1 rounded bg-red-600 px-1 py-px font-bold uppercase leading-none text-white ${
      wide ? 'text-[11px]' : 'text-[9px]'
    }`}
  >
    <span className="h-1 w-1 shrink-0 animate-pulse rounded-full bg-white" />
    {withWord && <span>Läuft</span>}
  </span>
)

/**
 * Amber, like the board's `Umdisponieren` pill. `relative z-10` lifts it above the ghost hatch the
 * shell lays over the block: the pill is the one part of a ghosted block that has to stay crisp.
 *
 * Not uppercase, unlike the board's: a three-day phone column is ~85px of text width, and
 * `WIRD UMGEPLANT` at 9px bold is wider than that. It used to be clipped to `WIRD UMGEP` by the
 * block's own `overflow-hidden`. Sentence case fits, and `truncate` is the last resort rather than
 * the normal case.
 */
export const ReschedulingMarker = ({
  withWord,
  wide = false,
}: {
  withWord: boolean
  wide?: boolean
}) => (
  <span
    className={`relative z-10 inline-flex max-w-full shrink-0 items-center gap-1 self-start rounded bg-amber-500 px-1 py-px font-bold text-white ${
      wide ? 'text-[12px] leading-[13px]' : 'text-[10px] leading-[11px]'
    }`}
  >
    <span className="h-1 w-1 shrink-0 rounded-full bg-white" />
    {withWord && <span className="truncate">{RESCHEDULING_LABEL}</span>}
  </span>
)

export const WorkBody = ({
  fit,
  wide = false,
  workMinutes,
  timeLabel,
  subject,
  who,
  contact,
  running = false,
  rescheduling = false,
  attachmentCount = 0,
}: Props) => {
  const { tier, lines } = fit
  const text = textFor(wide)

  // Nothing fits. The block's colour, its pulsing ring and its `aria-label` carry it from here.
  if (tier === 'bare') return null

  const marker = resolveRunningMarker(tier)

  const duration =
    wide && workMinutes != null && workMinutes > 0 ? (
      <span className={`${text.meta} shrink-0 tabular-nums`}>· {formatDurationDe(workMinutes)}</span>
    ) : null

  if (tier === 'single') {
    // One row, two facts. Kept as two elements rather than one joined string: the time is fixed-width
    // and must not be the thing that truncates, and the subject is what tests and search look for.
    return (
      <span className="flex min-w-0 items-center gap-1">
        <span className={`${text.time} shrink-0`}>{timeLabel}</span>
        {rescheduling && <ReschedulingMarker withWord={false} wide={wide} />}
        {running && marker !== 'ring' && <RunningMarker withWord={false} wide={wide} />}
        {subject && <span className={text.subject}>{subject}</span>}
        {duration && <span className="ml-auto shrink-0">{duration}</span>}
      </span>
    )
  }

  /*
   * The pill gets a row of its own once there are three to spend — beside the time it had to share a
   * ~85px column with it and was cut off. With only two rows it stays a dot after the time, and the
   * hatch and outline carry the rest. The row it takes comes out of what the others may use.
   */
  const pillRow = rescheduling && lines >= 3
  const free = lines - (pillRow ? 1 : 0)

  // Rows are spent in order; each `free >=` gate is one more row the block actually has.
  // Wide: one row for both, so it is paid for at three rows rather than three and four.
  const whoLine = wide ? [who, contact].filter(Boolean).join(' · ') : who
  const showWho = Boolean(whoLine) && free >= 3
  const showContact = !wide && Boolean(contact) && free >= 4
  const spent = 1 + (subject ? 1 : 0) + (showWho ? 1 : 0) + (showContact ? 1 : 0)
  // A row nobody claimed goes back to the subject — a long street name is the thing most likely to
  // have been cut, and the block that has a row to spare is exactly the one that can afford it.
  const subjectClamped = Boolean(subject) && free - spent >= 1

  return (
    <div className="flex h-full w-full min-w-0 flex-col justify-start">
      <span className="flex min-w-0 items-center gap-1">
        <span className={`${text.time} shrink-0`}>{timeLabel}</span>
        {duration}
        {rescheduling && !pillRow && <ReschedulingMarker withWord={false} wide={wide} />}
        {running && marker !== 'ring' && (
          <RunningMarker withWord={marker === 'pill' && lines >= 4} wide={wide} />
        )}
        {attachmentCount > 0 && (
          <span className="ml-auto shrink-0">
            <AttachmentBadge count={attachmentCount} inline />
          </span>
        )}
      </span>

      {pillRow && <ReschedulingMarker withWord wide={wide} />}

      {subject && (
        <span
          className={
            subjectClamped
              ? `line-clamp-2 break-words font-semibold ${
                  wide ? 'text-[15px] leading-[17px]' : 'text-[12px] leading-[13px]'
                }`
              : text.subject
          }
        >
          {subject}
        </span>
      )}

      {showWho && <span className={`${text.meta}${wide ? ' tabular-nums' : ''}`}>{whoLine}</span>}
      {showContact && <span className={`${text.meta} tabular-nums`}>{contact}</span>}
    </div>
  )
}
